import { Formik } from "formik";
import _ from "lodash";
import { useEffect, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import FileUpload from "../../components/FileUpload";
import Input from "../../components/input";
import Select from "../../components/Select";
import { getIssueById, getIssueTagsDropdown, getServiceEntryDropdown } from "../../store/Slices/IssuesSlice";
import { fetchVehicleDetail, fetchVehicleDropdown, updateServiceIssue } from "../../store/Slices/ServiceSlice";
import { getUserDropdown } from "../../store/Slices/UserSlice";
import { priorityOptions, statusOptions } from "../../utils/Dropdowns";
import { hasPermission } from "../../utils/helper";
import { IssueValidationSchema } from "../../utils/Schema";
import { errorNotify, successNotify } from "../../utils/toast";
import IssueHistory from "./IssueHistory";

const IssueDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();

  const { loading, vehicleData, serviceUpdateLoading } = useSelector((state) => state.services);
  const { userDropdownLoading, userDropdownData } = useSelector((state) => state.users);
  const { issueIdData, issueIdLoading, getIssueTagsDropdownData,
    getIssueTagsDropdownLoading } = useSelector((state) => state.issues);

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const canUpdate = hasPermission("issue", "update");

  useEffect(() => {
    dispatch(getUserDropdown());
    dispatch(fetchVehicleDropdown());
    dispatch(getIssueById(id));
    dispatch(getServiceEntryDropdown());
    dispatch(getIssueTagsDropdown())
  }, []);

  useEffect(() => {
    if (issueIdData) {
      setSelectedVehicle(issueIdData.vehicle);

      setUploadedFiles(
        issueIdData.images.map((img) => ({
          fileName: img.file_path,
          originalName: img.file_path,
          previewUrl: null,
          backend: true,
        }))
      );
    }
  }, [issueIdData]);

  const tagsOptions = getIssueTagsDropdownData?.map(tag => ({ label: tag.tag_name, value: tag.tag_name }));

  const seOptions = issueIdData
    ? issueIdData?.vehicle_services.map((item) => ({
      label: item?.service_id,
      value: item?.id,
    }))
    : [];

  const userOptions =
    userDropdownData &&
    userDropdownData.map((user) => ({
      value: user.value,
      label: user.label,
      image: user.image,
      showIcon: true,
    }));

  const handleVehicleChange = async (selectedOption, setFieldValue) => {
    setSelectedVehicle(selectedOption);
    setFieldValue("vehicle_id", selectedOption?.value || "");

    if (selectedOption) {
      const response = await dispatch(
        fetchVehicleDetail({
          vehicleId: selectedOption.value,
          vehicleBrandId: selectedOption.brand_id,
          ref_id: selectedOption.ref_id,
        })
      ).unwrap();

      setFieldValue('odo_meter', response?.data?.last_service?.odo_meter || null);
      setFieldValue('odm', response?.data?.odm || '');
    }
  };

  const initialValues = {
    vehicle_id: selectedVehicle?.id || "",
    service_id: issueIdData?.service_id || "",
    reported_by: issueIdData?.reported_by || "",
    assign_to: issueIdData?.assign_to || "",
    odo_meter: issueIdData?.last_service?.odo_meter || null,
    odm: issueIdData?.odo_meter || "",
    priority: issueIdData?.priority || "",
    status: issueIdData?.status || "",
    summery: issueIdData?.summery || "",
    description: issueIdData?.description || "",
    reported_at: issueIdData?.reported_at
      ? issueIdData.reported_at.slice(0, 16)
      : "",
    tags:
      issueIdData?.issue_tags?.map((tag) => ({
        value: tag.tag_name,
        label: tag.tag_name,
      })) || [],
    files: [],
    link_service_ids: issueIdData?.services?.map((s) => s.id) || [],
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const changedKeys = Object.keys(values).filter((key) => {
        return !_.isEqual(values[key], initialValues[key]);
      });

      const hasNewFiles = uploadedFiles.some((file) => file.previewUrl);

      if (hasNewFiles && !changedKeys.includes("files")) {
        changedKeys.push("files");
      }

      const issueData = {
        vehicle_id: parseInt(values.vehicle_id),
        issue_id: parseInt(id),
        service_id: parseInt(id),
        service_issue_id: issueIdData?.service_issue_id,
        reported_by: parseInt(values.reported_by),
        assign_to: parseInt(values.assign_to),
        odo_meter: parseInt(values.odm),
        priority: values.priority,
        issue_type: issueIdData?.issue_type,
        status: values.status,
        summery: values.summery,
        description: values.description,
        reported_at: new Date(values.reported_at).toISOString(),
        tags: values.tags.map((tag) => tag.value || tag),
        files: uploadedFiles.map((file) => file.fileName),
        link_service_ids: values.link_service_ids,
        updated_keys: changedKeys,
      };
      await dispatch(updateServiceIssue(issueData)).unwrap();
      successNotify("Issue Updated successfully");
      resetForm();
      navigate("/issue");
    } catch (error) {
      errorNotify(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (issueIdLoading) {
    return (
      <div className="loading_spinner">
        <Spinner animation="grow" />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumbs values={["Issues", "Overview", "Issue Details"]} />

      <Formik
        initialValues={initialValues}
        validationSchema={IssueValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          submitForm,
        }) => (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitForm();
            }}
          >
            <div className="access_view">
              <h2>Issue Details</h2>
              <div className="service_entry_btn">
                <Button
                  cancelBtn={() => {
                    navigate(-1);
                  }}
                  loading={isSubmitting || serviceUpdateLoading}
                  disabled={isSubmitting || serviceUpdateLoading}
                  submitText="Save Details"
                  type="submit"
                  shouldRow={false}
                  buttonExtraCss="save_issue_details_btn"
                  isShowSubmitBtn={canUpdate}
                />
              </div>
            </div>

            <div className="service_entry_main">
              <div className="container-fluid">
                <div className="row">
                  <div className="col-md-8">
                    <div className="service_entry_details">
                      <h6>Issue Main Information</h6>
                      <p>Comprehensive information for this issue record.</p>

                      <div className="vehicle_details_fields">
                        <Row>
                          <Col md={12}>
                            <div className="show_issue_id">
                              Issue ID: <span>SI-{id}</span>
                            </div>
                          </Col>
                          <Col md={12}>
                            <Input
                              label="Summary"
                              placeholder="Brief overview of the issue"
                              type="text"
                              name="summery"
                              value={values.summery}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.summery}
                              touched={touched.summery}
                            />
                          </Col>
                          <Col md={12}>
                            <Input
                              label="Description (Optional)"
                              placeholder="Add issue description"
                              type="text"
                              name="description"
                              value={values.description}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.description}
                              touched={touched.description}
                              quill={true}
                              setFieldValue={setFieldValue}
                            />
                          </Col>
                        </Row>
                      </div>
                    </div>

                    <div
                      className="service_entry_details"
                      style={{ marginTop: "20px" }}
                    >
                      <h6>Service Entry</h6>
                      <p>
                        Upload relevant photos or documents for this service.
                      </p>

                      <Row>
                        <Col md={12}>
                          <Select
                            label="Link Service Entry"
                            placeholder="Please Select Link Service Entry"
                            options={seOptions}
                            loading={userDropdownLoading}
                            name="link_service_ids"
                            value={seOptions?.filter((option) =>
                              values.link_service_ids.includes(option.value)
                            )}
                            onChange={(selectedOptions) =>
                              setFieldValue(
                                "link_service_ids",
                                selectedOptions
                                  ? selectedOptions.map((opt) => opt.value)
                                  : []
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.link_service_ids}
                            touched={touched.link_service_ids}
                            isSearch={false}
                            isMulti={true}
                          />
                        </Col>
                      </Row>
                    </div>

                    <div
                      className="service_entry_details"
                      style={{ marginTop: "20px" }}
                    >
                      <h6>Other Details</h6>
                      <p>Edit information for this issue record.</p>

                      <Row>
                        <Col md={6}>
                          <Select
                            label="Reporter"
                            placeholder="Please Select"
                            options={userOptions}
                            loading={userDropdownLoading}
                            name="reported_by"
                            value={userOptions.find(
                              (option) => option.value === values.reported_by
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "reported_by",
                                selectedOption?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.reported_by}
                            touched={touched.reported_by}
                            isSearch={false}
                            isDisabled={true}
                          />
                        </Col>

                        <Col md={6}>
                          <Select
                            label="Assignee"
                            placeholder="Please Select"
                            options={userOptions}
                            loading={userDropdownLoading}
                            name="assign_to"
                            value={userOptions.find(
                              (option) => option.value === values.assign_to
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "assign_to",
                                selectedOption?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.assign_to}
                            touched={touched.assign_to}
                            isSearch={false}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Priority"
                            placeholder="Select Priority"
                            options={priorityOptions}
                            loading={false}
                            name="priority"
                            value={priorityOptions.find(
                              (option) => option.value === values.priority
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "priority",
                                selectedOption?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.priority}
                            touched={touched.priority}
                            isSearch={false}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Status"
                            placeholder="Select Status"
                            options={statusOptions}
                            loading={false}
                            name="status"
                            value={statusOptions.find(
                              (option) => option.value === values.status
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "status",
                                selectedOption?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.status}
                            touched={touched.status}
                            isSearch={false}
                          />
                        </Col>
                        <Col md={6}>
                          <Input
                            label="Reported Date & Time"
                            placeholder=""
                            type="datetime-local"
                            name="reported_at"
                            value={values.reported_at}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.reported_at}
                            touched={touched.reported_at}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Vehicle"
                            placeholder="Select Vehicle"
                            options={vehicleData}
                            loading={loading}
                            name="vehicle_id"
                            value={vehicleData.find(
                              (option) => option.value === values.vehicle_id
                            )}
                            onChange={(selectedOption) =>
                              handleVehicleChange(selectedOption, setFieldValue)
                            }
                            onBlur={handleBlur}
                            error={errors.vehicle_id}
                            touched={touched.vehicle_id}
                            isSearch={false}
                          />
                        </Col>
                        <Col md={6}>
                          <Input
                            label="Odoo Meter"
                            placeholder="Enter Odoo Meter"
                            type="number"
                            name="odm"
                            showExtraText={issueIdData ? true : false}
                            extraText={`Last Recorded: ${values?.odo_meter || 0
                              } KM`}
                            value={values.odm}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.odm}
                            touched={touched.odm}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Tags"
                            placeholder="Add Tags"
                            options={tagsOptions}
                            loading={getIssueTagsDropdownLoading}
                            name="tags"
                            value={values.tags}
                            onChange={(selectedOptions) =>
                              setFieldValue("tags", selectedOptions || [])
                            }
                            onBlur={handleBlur}
                            isMulti={true}
                            isCreatable={true}
                            error={
                              errors.tags && typeof errors.tags === "string"
                                ? errors.tags
                                : null
                            }
                            touched={touched.tags}
                          />
                        </Col>
                      </Row>
                    </div>

                    <div className="photo_document_section mb-3">
                      <h6>Photos & Documents</h6>
                      <p>
                        Upload relevant photos or documents for this service.
                      </p>
                      <FileUpload
                        destination="service-entry"
                        files={uploadedFiles}
                        setFiles={setUploadedFiles}
                        maxFileSize={10}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <IssueHistory />
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default IssueDetails;
